import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export type InfractionSeverity = 'minor' | 'moderate' | 'severe' | 'critical';
export type InfractionStatus = 'pending' | 'confirmed' | 'dismissed';

export interface UserInfraction {
  id: string;
  user_id: string;
  reported_by: string | null;
  infraction_type: string;
  severity: InfractionSeverity;
  description: string;
  related_entity_type: string | null;
  related_entity_id: string | null;
  reviewed_by: string | null;
  status: InfractionStatus;
  created_at: string;
  expires_at: string | null;
  updated_at: string;
}

export interface AddInfractionParams {
  userId: string;
  infractionType: string;
  severity: InfractionSeverity;
  description: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

export interface AddInfractionResponse {
  success: boolean;
  strike_count: number;
  status: string;
  suspended_until: string | null;
}

export const addInfraction = async (params: AddInfractionParams): Promise<AddInfractionResponse> => {
  const { data, error } = await supabase.rpc('admin_add_infraction', {
    p_user_id: params.userId,
    p_infraction_type: params.infractionType,
    p_severity: params.severity,
    p_description: params.description,
    p_related_entity_type: params.relatedEntityType || null,
    p_related_entity_id: params.relatedEntityId || null,
  });

  if (error) {
    throw new Error(error.message || 'Error adding infraction');
  }

  return data as unknown as AddInfractionResponse;
};

export const getUserInfractions = async (userId: string): Promise<UserInfraction[]> => {
  const { data, error } = await supabase
    .from('user_infractions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Error fetching infractions');
  }

  return (data || []) as UserInfraction[];
};

// Hook to get translated infraction types
export const useInfractionTypes = () => {
  const { t } = useTranslation();
  
  return useMemo(() => [
    { value: 'spam', label: t('infractions.types.spam', 'Spam') },
    { value: 'harassment', label: t('infractions.types.harassment', 'Harassment') },
    { value: 'inappropriate_content', label: t('infractions.types.inappropriate_content', 'Inappropriate Content') },
    { value: 'hate_speech', label: t('infractions.types.hate_speech', 'Hate Speech') },
    { value: 'violence', label: t('infractions.types.violence', 'Violence') },
    { value: 'illegal_content', label: t('infractions.types.illegal_content', 'Illegal Content') },
    { value: 'impersonation', label: t('infractions.types.impersonation', 'Impersonation') },
    { value: 'false_information', label: t('infractions.types.false_information', 'False Information') },
    { value: 'copyright', label: t('infractions.types.copyright', 'Copyright Violation') },
    { value: 'other', label: t('infractions.types.other', 'Other') },
  ], [t]);
};

// Hook to get translated severity info
export const useSeverityInfo = () => {
  const { t } = useTranslation();
  
  return useMemo(() => ({
    minor: {
      label: t('infractions.severity.minor.label', 'Minor'),
      description: t('infractions.severity.minor.description', 'Minor infractions (spam, inappropriate language)'),
      color: 'bg-yellow-100 text-yellow-800',
    },
    moderate: {
      label: t('infractions.severity.moderate.label', 'Moderate'),
      description: t('infractions.severity.moderate.description', 'Moderate infractions (mild harassment, offensive content)'),
      color: 'bg-orange-100 text-orange-800',
    },
    severe: {
      label: t('infractions.severity.severe.label', 'Severe'),
      description: t('infractions.severity.severe.description', 'Severe infractions (harassment, threats)'),
      color: 'bg-red-100 text-red-800',
    },
    critical: {
      label: t('infractions.severity.critical.label', 'Critical'),
      description: t('infractions.severity.critical.description', 'Critical infractions (illegal content, violence) - Immediate ban'),
      color: 'bg-red-900 text-white',
    },
  }), [t]);
};

// Static versions for non-React contexts (backwards compatibility)
export const INFRACTION_TYPES = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'inappropriate_content', label: 'Inappropriate Content' },
  { value: 'hate_speech', label: 'Hate Speech' },
  { value: 'violence', label: 'Violence' },
  { value: 'illegal_content', label: 'Illegal Content' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'false_information', label: 'False Information' },
  { value: 'copyright', label: 'Copyright Violation' },
  { value: 'other', label: 'Other' },
];

export const SEVERITY_INFO = {
  minor: {
    label: 'Minor',
    description: 'Minor infractions (spam, inappropriate language)',
    color: 'bg-yellow-100 text-yellow-800',
  },
  moderate: {
    label: 'Moderate',
    description: 'Moderate infractions (mild harassment, offensive content)',
    color: 'bg-orange-100 text-orange-800',
  },
  severe: {
    label: 'Severe',
    description: 'Severe infractions (harassment, threats)',
    color: 'bg-red-100 text-red-800',
  },
  critical: {
    label: 'Critical',
    description: 'Critical infractions (illegal content, violence) - Immediate ban',
    color: 'bg-red-900 text-white',
  },
};

type TranslateFunction = (key: string, options?: Record<string, unknown>) => string;

export const getStrikePenalty = (strikeCount: number, severity: InfractionSeverity, t?: TranslateFunction) => {
  const translate = (key: string, defaultValue: string) => {
    if (t) {
      return t(key, { defaultValue }) as string;
    }
    return defaultValue;
  };
  
  if (severity === 'critical') {
    return {
      action: translate('infractions.penalties.permanentBan', 'Permanent Ban'),
      duration: translate('infractions.penalties.permanent', 'Permanent'),
      strike: 4,
    };
  }

  switch (strikeCount + 1) {
    case 1:
      return {
        action: translate('infractions.penalties.warning', 'Warning (Warned)'),
        duration: translate('infractions.penalties.24hours', '24 hours'),
        strike: 1,
      };
    case 2:
      return {
        action: translate('infractions.penalties.suspension', 'Suspension'),
        duration: translate('infractions.penalties.7days', '7 days'),
        strike: 2,
      };
    case 3:
      return {
        action: translate('infractions.penalties.extendedSuspension', 'Extended Suspension'),
        duration: translate('infractions.penalties.30days', '30 days'),
        strike: 3,
      };
    default:
      return {
        action: translate('infractions.penalties.permanentBan', 'Permanent Ban'),
        duration: translate('infractions.penalties.permanent', 'Permanent'),
        strike: 4,
      };
  }
};
